import React, { createContext, useContext, useEffect, useState } from "react";
import api, { setToken, clearToken, getToken } from "../lib/api";

const AuthContext = createContext(null);

// Turn an axios error into a clear, human message (network vs server vs validation).
const readError = (err, fallback) => {
  if (err?.response) {
    const detail = err.response.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
    if (err.response.status === 401) return "Invalid email or password.";
    return fallback;
  }
  if (err?.request) return "Can't reach the server. Please check your connection and try again.";
  return fallback;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      if (getToken()) {
        try {
          const { data } = await api.get("/auth/me");
          setUser(data.user);
        } catch (e) {
          clearToken();
        }
      }
      setLoading(false);
    };
    init();
  }, []);

  const register = async ({ username, email, password }) => {
    try {
      const { data } = await api.post("/auth/register", { username, email, password });
      setToken(data.token);
      setUser(data.user);
      return data.user;
    } catch (err) {
      throw new Error(readError(err, "Registration failed."));
    }
  };

  const login = async ({ email, password }) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.token);
      setUser(data.user);
      return data.user;
    } catch (err) {
      throw new Error(readError(err, "Login failed."));
    }
  };

  const logout = () => {
    clearToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, register, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
